import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Link as MuiLink,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  RecordVoiceOver,
  NoteAdd,
  FolderOpen,
  Business,
  ContentCopy,
  OpenInNew,
  SmartToy,
  LinkOff,
  AutoAwesome,
  Search,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAIRequests } from '../context/AIRequestContext';
import { supabase } from '../lib/supabase';
import edgeFunctionService from '../services/edgeFunctionService';
import {
  getExtractionTypeOptions,
  extractionConfigToLegacyExtractionTypeArray,
  normalizeExtractionConfig,
} from '../utils/extractionTaxonomy';
import { isMissingColumnError } from '../utils/supabaseSchemaFallback';
import { customerPath, projectPath } from '../utils/navigation';
import RequirementsTab from './RequirementsTab';
import SessionEditorDialog from './SessionEditorDialog';
import SummaryActionsTab from './SummaryActionsTab';
import EntityBreadcrumbs from './layout/EntityBreadcrumbs';
import TranscriptView from './sessions/TranscriptView';
import { normaliseTranscript, parseExtractionTypeLabels } from './sessions/transcriptUtils';
import {
  PageContainer,
  PageHeader,
  SectionCard,
  EmptyState,
  ConfirmDialog,
  askInkyOrCopy,
  describeInkyOutcome,
} from './pagekit';
import { formatDateTime } from '../utils/dateUtils';

/** Tab keys; `?tab=` uses these. */
const TABS = ['general', 'summary', 'requirements'];

/**
 * Source label chip.
 * @param {object} props
 * @param {string} props.source
 * @returns {JSX.Element}
 */
function SourceChip({ source }) {
  const map = {
    fathom: { label: 'Fathom', icon: <RecordVoiceOver fontSize="inherit" />, color: 'info' },
    recall: { label: 'Notetaker', icon: <SmartToy fontSize="inherit" />, color: 'primary' },
    manual: { label: 'Upload', icon: <NoteAdd fontSize="inherit" />, color: 'default' },
  };
  const def = map[source] || map.manual;
  return <Chip icon={def.icon} label={def.label} size="small" color={def.color} />;
}

/**
 * Session (recording) page: details and transcript, AI summary and
 * actions, and the requirements extracted from it. Tabs are synced to `?tab=`.
 * @returns {JSX.Element} Session hub page.
 */
function SessionHubPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, actions } = useApp();
  const { refreshRequests } = useAIRequests();

  const [transcript, setTranscript] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);

  // Fallback fetch for unlinked sessions not in state.projects
  const [fetchedSession, setFetchedSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [unlinkError, setUnlinkError] = useState('');

  const [regenMode, setRegenMode] = useState('delete');
  const [regenError, setRegenError] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [toast, setToast] = useState('');

  // Ref to prevent double-firing the transcript load (avoids dep-loop on loadingTranscript)
  const transcriptLoadStarted = useRef(false);

  /**
   * Derive the session and its parent project/customer from global app state.
   * Falls back to a directly-fetched session for unlinked sessions (project_id: null).
   */
  const { session, project, customer } = useMemo(() => {
    for (const proj of state.projects || []) {
      const sess = (proj.sessions || []).find((s) => s.id === sessionId);
      if (sess) {
        const cust = (state.customers || []).find((c) => c.id === proj.customer_id) || proj.customer || null;
        return { session: sess, project: proj, customer: cust };
      }
    }
    if (fetchedSession) {
      return {
        session: {
          ...fetchedSession,
          extractionType: fetchedSession.extraction_type,
          extractionConfig: fetchedSession.extraction_config ?? null,
          sourceMetadata: fetchedSession.source_metadata,
          createdAt: fetchedSession.created_at,
        },
        project: null,
        customer: null,
      };
    }
    return { session: null, project: null, customer: null };
  }, [sessionId, state.projects, state.customers, fetchedSession]);

  /** Session handed to the editor, with the loaded transcript attached. */
  const editorSession = useMemo(
    () => (session && transcript ? { ...session, transcript } : session),
    [session, transcript]
  );

  const tabParam = searchParams.get('tab');
  const availableTabs = project ? TABS : TABS.filter((t) => t !== 'requirements');
  const activeTab = availableTabs.includes(tabParam) ? tabParam : 'general';

  const handleTabChange = useCallback((_, value) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * If the session isn't in state.projects, fetch its metadata directly from Supabase.
   * This covers Recall sessions that haven't been linked to a project yet.
   */
  useEffect(() => {
    if (state.loading || session) return undefined;
    let cancelled = false;
    setLoadingSession(true);
    const selectFull = 'id, name, description, source, source_metadata, extraction_type, extraction_config, created_at, summary';
    const selectLegacy = 'id, name, description, source, source_metadata, extraction_type, created_at, summary';

    (async () => {
      let { data, error } = await supabase
        .from('sessions')
        .select(selectFull)
        .eq('id', sessionId)
        .single();
      if (isMissingColumnError(error, 'extraction_config')) {
        ({ data, error } = await supabase
          .from('sessions')
          .select(selectLegacy)
          .eq('id', sessionId)
          .single());
      }
      if (cancelled) return;
      if (!error && data) setFetchedSession(data);
      setLoadingSession(false);
    })();

    return () => { cancelled = true; };
  }, [sessionId, session, state.loading]);

  /** Extraction types for the session's requirements list. */
  const extractionTypes = useMemo(() => getExtractionTypeOptions(), []);

  // Reset transcript state when the session changes
  useEffect(() => {
    setTranscript(null);
    setTranscriptError(null);
    setLoadingTranscript(false);
    transcriptLoadStarted.current = false;
  }, [sessionId]);

  /** Lazy-load the full transcript when the General tab is shown. */
  useEffect(() => {
    if (activeTab !== 'general' || !sessionId || transcript || transcriptLoadStarted.current) return undefined;
    transcriptLoadStarted.current = true;
    let cancelled = false;
    async function load() {
      setLoadingTranscript(true);
      setTranscriptError(null);
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select('transcript')
          .eq('id', sessionId)
          .single();
        if (error) throw error;
        if (!cancelled) setTranscript(normaliseTranscript(data?.transcript));
      } catch (err) {
        if (!cancelled) {
          setTranscriptError('Failed to load transcript.');
          transcriptLoadStarted.current = false; // allow retry
        }
      } finally {
        if (!cancelled) setLoadingTranscript(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeTab, sessionId, transcript]);

  /**
   * Save edits from the editor dialog.
   * @param {object} updates - Updated session fields.
   * @returns {Promise<object|undefined>} Update result.
   */
  const handleSaveEdit = useCallback(async (updates) => {
    if (!project || !session) return { success: false, error: 'Link this session to a project to edit it.' };
    setSavingEdit(true);
    const result = await actions.updateSession(project.id, session.id, updates);
    setSavingEdit(false);
    if (result.success) {
      setEditOpen(false);
      if (updates.transcript !== undefined) {
        setTranscript(normaliseTranscript(updates.transcript));
      }
    }
    return result;
  }, [actions, project, session]);

  /** Trigger requirement regeneration for this session. */
  const handleRegenRequirements = useCallback(async () => {
    if (!session || !project) return;
    setRegenLoading(true);
    setRegenError('');
    try {
      const cfg = normalizeExtractionConfig(session.extractionConfig, session.extractionType);
      const response = await edgeFunctionService.callFunction('extract-requirements', {
        sessionId: session.id,
        projectId: project.id,
        extractionTypes: extractionConfigToLegacyExtractionTypeArray(cfg),
        extractionConfig: cfg,
        targetedExtraction: '',
        projectContext: project.description || project.name || '',
        isRegeneration: true,
        regenerateMode: regenMode,
      });
      if (!response?.success) throw new Error(response?.error || 'Extraction failed');
      await refreshRequests();
      setToast('Extraction started. Requirements update when the job finishes.');
    } catch (err) {
      setRegenError(err.message);
    } finally {
      setRegenLoading(false);
    }
  }, [session, project, regenMode, refreshRequests]);

  /** Unlink this session from its project (sets project_id to null). */
  const handleUnlink = useCallback(async () => {
    if (!project || !session) return;
    setUnlinkLoading(true);
    setUnlinkError('');
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ project_id: null })
        .eq('id', session.id);
      if (error) throw error;
      setUnlinkConfirmOpen(false);
      await actions.loadProjects();
      navigate('/customers/sessions');
    } catch (err) {
      setUnlinkError(err.message || 'Failed to unlink session.');
      setUnlinkConfirmOpen(false);
    } finally {
      setUnlinkLoading(false);
    }
  }, [project, session, actions, navigate]);

  const handleAskInky = useCallback(async () => {
    if (!session) return;
    const outcome = await askInkyOrCopy(
      `Summarize the meeting "${session.name || 'Untitled session'}"${project ? ` in the project "${project.name}"` : ''}: ` +
      'the key decisions, action items with owners, open questions, and any requirements we have not captured yet.',
      { context: { surface: 'session', sessionId: session.id, projectId: project?.id } }
    );
    setToast(describeInkyOutcome(outcome));
  }, [session, project]);

  if (state.loading || loadingSession) {
    return (
      <PageContainer aria-busy="true" aria-label="Loading session">
        <Skeleton variant="text" width={240} />
        <Skeleton variant="text" width="50%" height={44} />
        <Skeleton variant="rounded" height={44} sx={{ borderRadius: 'var(--q-radius-xl)' }} />
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 'var(--q-radius-card)' }} />
      </PageContainer>
    );
  }

  if (!session) {
    return (
      <PageContainer width="narrow">
        <SectionCard>
          <EmptyState
            icon={<Search />}
            title="Session not found"
            description="It may have been deleted, or you may not have access to it."
            actions={<Button variant="contained" onClick={() => navigate('/customers/sessions')}>All sessions</Button>}
          />
        </SectionCard>
      </PageContainer>
    );
  }

  const requirementCount = project
    ? (project.requirements || []).filter((r) => r.sessionId === session.id).length
    : 0;

  return (
    <PageContainer>
      <PageHeader
        breadcrumbs={<EntityBreadcrumbs sessionId={session.id} projectId={project?.id} current={session.name || 'Untitled session'} />}
        title={session.name || 'Untitled session'}
        actions={
          <>
            {project && (
              <Tooltip title="Remove this session from its project">
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<LinkOff />}
                  onClick={() => setUnlinkConfirmOpen(true)}
                  disabled={unlinkLoading}
                  sx={{ color: 'text.secondary' }}
                >
                  Unlink
                </Button>
              </Tooltip>
            )}
            {project && (
              <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            <Button variant="contained" startIcon={<AutoAwesome />} onClick={handleAskInky}>
              Ask Inky
            </Button>
          </>
        }
        meta={
          <>
            <SourceChip source={session.source} />
            {session.createdAt && (
              <Typography variant="body2" color="text.secondary">{formatDateTime(session.createdAt)}</Typography>
            )}
            {customer && (
              <Chip icon={<Business fontSize="inherit" />} label={customer.name} size="small" variant="outlined" component={Link} to={customerPath(customer.id)} clickable />
            )}
            {project && (
              <Chip icon={<FolderOpen fontSize="inherit" />} label={project.name} size="small" variant="outlined" component={Link} to={projectPath(project.id, 'sessions')} clickable />
            )}
          </>
        }
      />

      {!project && (
        <Alert
          severity="info"
          icon={<LinkOff fontSize="inherit" />}
          action={<Button color="inherit" size="small" onClick={() => navigate('/home')}>Link on Home</Button>}
        >
          This recording is not linked to a project yet. Link it to extract requirements and use it in documents.
        </Alert>
      )}

      {unlinkError && (
        <Alert severity="error" onClose={() => setUnlinkError('')}>{unlinkError}</Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="Session sections">
          <Tab value="general" label="Overview" id="session-tab-general" aria-controls="session-panel" />
          <Tab value="summary" label="Summary & actions" id="session-tab-summary" aria-controls="session-panel" />
          {project && (
            <Tab value="requirements" label={`Requirements (${requirementCount})`} id="session-tab-requirements" aria-controls="session-panel" />
          )}
        </Tabs>
      </Box>

      <Box id="session-panel" role="tabpanel" aria-labelledby={`session-tab-${activeTab}`} sx={{ minWidth: 0 }}>
        {activeTab === 'general' && (
          <GeneralTab
            session={session}
            project={project}
            customer={customer}
            transcript={transcript}
            loadingTranscript={loadingTranscript}
            transcriptError={transcriptError}
            requirementCount={requirementCount}
            onViewRequirements={project ? () => handleTabChange(null, 'requirements') : undefined}
          />
        )}

        {activeTab === 'summary' && (
          <SummaryActionsTab
            session={session}
            project={project}
            regenLoading={regenLoading}
            regenError={regenError}
            onRegenRequirements={handleRegenRequirements}
            regenMode={regenMode}
            onRegenModeChange={setRegenMode}
          />
        )}

        {activeTab === 'requirements' && project && (
          <RequirementsTab
            project={project}
            filterSessionId={session.id}
            onAddRequirement={(requirementData) => actions.addRequirement({
              projectId: project.id,
              sessionId: session.id,
              ...requirementData,
            })}
            onUpdateRequirement={(reqId, updates) => actions.updateRequirement(project.id, reqId, updates)}
            onDeleteRequirement={(reqId) => actions.deleteRequirement(project.id, reqId)}
            extractionTypes={extractionTypes}
          />
        )}
      </Box>

      {editOpen && (
        <SessionEditorDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          session={editorSession}
          project={project}
          onSave={handleSaveEdit}
          saving={savingEdit}
        />
      )}

      <ConfirmDialog
        open={unlinkConfirmOpen}
        title="Unlink session?"
        message={`"${session.name || 'This session'}" is removed from ${project?.name || 'its project'} and moves to Unlinked Sessions on Home. Its transcript is kept.`}
        confirmLabel="Unlink"
        busy={unlinkLoading}
        onConfirm={handleUnlink}
        onClose={() => setUnlinkConfirmOpen(false)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageContainer>
  );
}

/**
 * Overview tab: session details and the transcript.
 * @param {object} props
 * @param {object} props.session
 * @param {object|null} props.project
 * @param {object|null} props.customer
 * @param {object|null} props.transcript
 * @param {boolean} props.loadingTranscript
 * @param {string|null} props.transcriptError
 * @param {number} props.requirementCount
 * @param {Function} [props.onViewRequirements]
 * @returns {JSX.Element}
 */
function GeneralTab({ session, project, customer, transcript, loadingTranscript, transcriptError, requirementCount, onViewRequirements }) {
  const [copied, setCopied] = useState(false);
  const plainContent = transcript?.content || '';

  const handleCopy = useCallback(() => {
    if (!plainContent || !navigator.clipboard) return;
    navigator.clipboard.writeText(plainContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [plainContent]);

  // Safe string helpers for sourceMetadata fields that may be objects
  const organizerStr = session.sourceMetadata?.organizer
    ? (typeof session.sourceMetadata.organizer === 'object'
      ? session.sourceMetadata.organizer?.name || session.sourceMetadata.organizer?.email || ''
      : session.sourceMetadata.organizer)
    : '';

  const participantsStr = Array.isArray(session.sourceMetadata?.participants)
    ? session.sourceMetadata.participants
      .map((p) => (typeof p === 'object' ? (p.name || p.email || p.display_name || '') : String(p)))
      .filter(Boolean)
      .join(', ')
    : '';

  const extractionLabels = parseExtractionTypeLabels(session.extractionType);

  const meetingRow = (() => {
    const mu = session.sourceMetadata?.meeting_url;
    if (!mu) return null;
    if (typeof mu === 'object') {
      const platform = mu.platform || '';
      const label = platform === 'microsoft_teams' ? 'Microsoft Teams'
        : platform ? platform.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : 'Meeting';
      return <InfoRow label="Meeting" value={label} />;
    }
    return <InfoRow label="Meeting URL" value={mu} href={mu} />;
  })();

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 340px) minmax(0, 1fr)' }, alignItems: 'start' }}>
      <SectionCard title="Details">
        <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1.25 }}>
          {session.description && <InfoRow label="Description" value={session.description} />}
          {customer && <InfoRow label="Customer" value={customer.name} linkTo={customerPath(customer.id)} />}
          <InfoRow
            label="Project"
            value={project ? project.name : 'Not linked'}
            linkTo={project ? projectPath(project.id) : undefined}
          />
          <InfoRow label="Source" value={session.source === 'recall' ? 'Quill AI notetaker' : session.source === 'fathom' ? 'Fathom' : 'Upload'} />
          {session.createdAt && <InfoRow label="Created" value={formatDateTime(session.createdAt)} />}
          {extractionLabels.length > 0 && <InfoRow label="Extraction" value={extractionLabels.join(', ')} />}
          {meetingRow}
          {organizerStr && <InfoRow label="Organizer" value={organizerStr} />}
          {participantsStr && <InfoRow label="Participants" value={participantsStr} />}
          {project && (
            <InfoRow
              label="Requirements"
              value={`${requirementCount} extracted`}
              onClick={onViewRequirements}
            />
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Transcript"
        actions={plainContent ? (
          <Tooltip title={copied ? 'Copied' : 'Copy transcript'}>
            <IconButton size="small" onClick={handleCopy} aria-label="Copy transcript">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      >
        {loadingTranscript && <Skeleton variant="rounded" height={280} sx={{ borderRadius: 'var(--q-radius-xl)' }} />}
        {!loadingTranscript && transcriptError && <Alert severity="error">{transcriptError}</Alert>}
        {!loadingTranscript && !transcriptError && !plainContent && (
          <Typography variant="body2" color="text.secondary">No transcript is stored for this session.</Typography>
        )}
        {!loadingTranscript && plainContent && <TranscriptView transcript={transcript} maxHeight={560} searchable />}
      </SectionCard>
    </Box>
  );
}

/**
 * A labelled row in the details list.
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 * @param {string} [props.linkTo] - Internal router path.
 * @param {string} [props.href] - External href.
 * @param {Function} [props.onClick] - Makes the value a button.
 * @returns {JSX.Element}
 */
function InfoRow({ label, value, linkTo, href, onClick }) {
  const safeValue = value !== null && value !== undefined && typeof value === 'object'
    ? JSON.stringify(value)
    : String(value ?? '');
  let content;
  if (href) {
    content = (
      <MuiLink href={href} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, overflowWrap: 'anywhere' }}>
        {safeValue} <OpenInNew fontSize="inherit" />
      </MuiLink>
    );
  } else if (linkTo) {
    content = <MuiLink component={Link} to={linkTo} variant="body2">{safeValue}</MuiLink>;
  } else if (onClick) {
    content = <MuiLink component="button" type="button" variant="body2" onClick={onClick} sx={{ textAlign: 'left' }}>{safeValue}</MuiLink>;
  } else {
    content = <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{safeValue}</Typography>;
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: 1 }}>
      <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>{content}</Box>
    </Box>
  );
}

export default SessionHubPage;
